/**
 * Sync product title line count per carousel — all cards match the tallest title (1–3 lines).
 */
(function () {
  const MAX_LINES = 3;
  const TITLE_SELECTOR = '.wd-fbt-card__title';
  const RESIZE_DEBOUNCE_MS = 150;

  let measureRoot;

  function getMeasureRoot() {
    if (!measureRoot) {
      measureRoot = document.createElement('div');
      measureRoot.setAttribute('aria-hidden', 'true');
      measureRoot.style.cssText =
        'position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:0;overflow:visible;';
      document.body.appendChild(measureRoot);
    }
    return measureRoot;
  }

  function measureTitleLines(title, width) {
    if (!width) {
      return 1;
    }

    const clone = title.cloneNode(true);
    clone.removeAttribute('href');
    clone.style.cssText =
      'width:' +
      width +
      'px;-webkit-line-clamp:unset;line-clamp:unset;overflow:visible;display:block;min-height:0;';

    const root = getMeasureRoot();
    root.appendChild(clone);
    const lineHeight = parseFloat(getComputedStyle(clone).lineHeight);
    const lines =
      lineHeight && !Number.isNaN(lineHeight)
        ? Math.min(MAX_LINES, Math.max(1, Math.round(clone.scrollHeight / lineHeight)))
        : 1;
    clone.remove();

    return lines;
  }

  function carouselFingerprint(carousel) {
    const titles = carousel.querySelectorAll(TITLE_SELECTOR);
    const parts = [titles.length];

    titles.forEach((title) => {
      parts.push(title.textContent);
      parts.push(Math.round(title.getBoundingClientRect().width));
    });

    return parts.join('\0');
  }

  function findCarousels(root) {
    const carousels = new Set();
    (root || document).querySelectorAll(TITLE_SELECTOR).forEach((title) => {
      const carousel = title.closest('scroll-carousel');
      if (carousel) {
        carousels.add(carousel);
      }
    });
    return carousels;
  }

  const syncState = new WeakMap();
  const resizeObservers = new WeakMap();

  function syncCarousel(carousel) {
    const titles = carousel.querySelectorAll(TITLE_SELECTOR);
    if (!titles.length) {
      return;
    }

    const fingerprint = carouselFingerprint(carousel);
    const cached = syncState.get(carousel);
    if (cached && cached.fingerprint === fingerprint) {
      return;
    }

    let maxLines = 1;
    titles.forEach((title) => {
      maxLines = Math.max(maxLines, measureTitleLines(title, title.getBoundingClientRect().width));
    });

    if (!cached || cached.maxLines !== maxLines) {
      carousel.style.setProperty('--wd-fbt-title-lines', String(maxLines));
    }

    syncState.set(carousel, { fingerprint, maxLines });
  }

  function disconnectCarousel(carousel) {
    syncState.delete(carousel);

    const ro = resizeObservers.get(carousel);
    if (ro) {
      ro.disconnect();
      resizeObservers.delete(carousel);
    }
  }

  function bind(carousel) {
    syncCarousel(carousel);

    if (resizeObservers.has(carousel)) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    let timer;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => syncCarousel(carousel), RESIZE_DEBOUNCE_MS);
    });
    ro.observe(carousel);
    resizeObservers.set(carousel, ro);
  }

  function scan(root) {
    findCarousels(root).forEach(bind);
  }

  function teardown(root) {
    findCarousels(root).forEach(disconnectCarousel);
  }

  function boot(root) {
    scan(root);
  }

  function init() {
    if (window.customElements?.get('scroll-carousel')) {
      boot();
      return;
    }

    window.customElements?.whenDefined('scroll-carousel').then(() => boot());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => boot());
  }
})();
